// src/services/ModernMessagingService.js
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../config';
import Cookies from 'js-cookie';
import encryptionService from './EncryptionService';

class ModernMessagingService {
  constructor() {
    this.socket = null;
    this.messageQueue = [];
    this.retryAttempts = 3;
    this.reconnectDelay = 1000;
    this.isOnline = navigator.onLine;
    this.listeners = new Map();
    this._newMessageListeners = new Set();
    this._messageUpdateListeners = new Set();

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processMessageQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // Get authentication headers
  getAuthHeaders() {
    const token = Cookies.get('authToken');

    return token ? { Authorization: `Token ${token}` } : {};
  }

  // =====================
  // CONVERSATION METHODS
  // =====================

  /**
   * Get all conversations for a user
   * @param {number} userId - User ID
   * @param {number} page - Page number (default: 1)
   * @returns {Promise<Object>} Conversations response
   */
  async getConversations(userId, page = 1) {
    try {
      const response = await axios.get(
        `${config.backendUrl}/messaging/chatroom/${userId}/`,
        {
          params: { page },
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get or create a conversation for a specific product
   * @param {number} productId - Product ID
   * @param {number} userId - Current user ID
   * @returns {Promise<Object>} Conversation data
   */
  async getOrCreateProductConversation(productId, userId) {
    try {
      const response = await axios.get(
        `${config.backendUrl}/messaging/product/${productId}/conversation/${userId}/`,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting/creating product conversation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get messages for a specific conversation
   * @param {number} conversationId - Conversation ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Messages response
   */
  async getConversationMessages(conversationId, userId) {
    try {
      const response = await axios.get(
        `${config.backendUrl}/messaging/chatroom/${conversationId}/${userId}`,
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      throw this.handleError(error);
    }
  }

  // ==================
  // MESSAGE METHODS
  // ==================

  /**
   * Send a message with smart retry and queuing
   * @param {Object} messageData - Message data
   * @returns {Promise<Object>} Message response
   */
  async sendMessage(messageData) {
    const clientId = messageData.client_id || uuidv4();
    const message = {
      ...messageData,
      id: clientId,
      client_id: clientId,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retries: 0,
    };

    // ❌ DISABLED: Frontend no longer encrypts messages - backend handles all encryption
    // This ensures consistent encryption/decryption across web and mobile apps
    // if (message.content && typeof message.content === 'string') {
    //   try {
    //     message.content = await encryptionService.encryptMessageContent(message.content);
    //   } catch (error) {
    //     console.error('❌ Failed to encrypt message, sending unencrypted:', error);
    //   }
    // }

    // Add to queue first (optimistic update)
    this.addToQueue(message);

    try {
      if (this.isOnline) {
        return await this.sendViaREST(message);
      } else {
        // Queue for later when back online
        message.status = 'queued';
        return message;
      }
    } catch (error) {
      return await this.handleSendError(message, error);
    }
  }

  /**
   * Send message via REST API
   * @param {Object} message - Message object
   * @returns {Promise<Object>} API response
   */
  async sendViaREST(message) {
    try {
      const formData = new FormData();
      formData.append('sender', message.sender);
      formData.append('recipient', message.recipient);
      formData.append('content', message.content);
      formData.append('product', message.product);
      formData.append('client_id', message.client_id || message.id);

      if (message.conversation) {
        formData.append('conversation', message.conversation);
      }

      // Handle attachments
      if (message.attachments && message.attachments.length > 0) {
        message.attachments.forEach((file) => {
          formData.append('attachment_files', file);
        });
      }

      const response = await axios.post(
        `${config.backendUrl}/messaging/messages/`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      message.status = 'sent';
      this.removeFromQueue(message.id);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send message via WebSocket
   * @param {Object} message - Message object
   * @returns {Promise<Object>} Success response
   */
  async sendViaWebSocket(message) {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      // Add response listener
      const messageId = message.id;
      const timeout = setTimeout(() => {
        this.listeners.delete(messageId);
        reject(new Error('WebSocket message timeout'));
      }, 30000); // 30 second timeout

      this.listeners.set(messageId, (response) => {
        clearTimeout(timeout);
        if (response.success) {
          message.status = 'sent';
          this.removeFromQueue(message.id);
          resolve(response.data);
        } else {
          reject(new Error(response.error || 'WebSocket send failed'));
        }
      });

      // Send message - Backend expects message data at root level, not nested in 'data'
      this.socket.send(JSON.stringify({
        ...message,
        messageId, // Keep messageId for response tracking
      }));
    });
  }

  /**
   * Get messages with pagination support (OPTIMIZED)
   * Uses the optimized chatroom endpoint with pagination and user details
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Messages response with user details
   */
  async getMessages(params = {}) {
    try {

      // Use the optimized chatroom endpoint for better performance
      let url;
      
      if (params.conversation_id && params.user_id) {
        // Use conversation-specific optimized endpoint
        url = `${config.backendUrl}/messaging/chatroom/${params.conversation_id}/${params.user_id}/`;
      } else if (params.product_id && params.user_id) {
        // Use product-specific optimized endpoint
        url = `${config.backendUrl}/messaging/chatroom/product/${params.product_id}/${params.user_id}/`;
      } else {
        // Fallback to general messages endpoint
        url = `${config.backendUrl}/messaging/messages/`;
      }

      // Add pagination parameters
      const queryParams = {
        page: params.page || 1,
        page_size: params.page_size || 20
      };

      const response = await axios.get(url, {
        params: queryParams,
        headers: this.getAuthHeaders(),
      });

      // Decrypt messages after receiving
      if (response.data.results && Array.isArray(response.data.results)) {
        response.data.results = await encryptionService.processMessages(response.data.results);
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      console.error('Error details:', error.response?.data);
      throw this.handleError(error);
    }
  }

  // ==================
  // WEBSOCKET METHODS
  // ==================

  /**
   * Connect to WebSocket for real-time messaging
   * @param {string} socketUrl - WebSocket URL
   */
  connectWebSocket(socketUrl) {
    if (this.socket) {
      this.socket.close();
    }

    try {
      this.socket = new WebSocket(socketUrl);

      this.socket.onopen = () => {

        this.processMessageQueue(); // Process any queued messages
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = (event) => {

        if (!event.wasClean) {
          // Attempt to reconnect after delay
          setTimeout(() => {
            this.connectWebSocket(socketUrl);
          }, this.reconnectDelay);
        }
      };

      this.socket.onerror = (error) => {
        console.error('ModernMessagingService: WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
    }
  }

  /**
   * Handle incoming WebSocket messages
   * @param {Object} data - WebSocket message data
   */
  async handleWebSocketMessage(data) {
    const incomingMessage = data.message || data.data?.message || data.data;

    // Handle response to sent message
    if (data.type === 'message_response' && data.messageId) {
      const listener = this.listeners.get(data.messageId);
      if (listener) {
        listener(data);
        this.listeners.delete(data.messageId);
      }
    }

    // Handle incoming new messages
      if (data.type === 'new_message' || data.type === 'chat_message') {
      // Decrypt message before emitting to listeners
      let message = incomingMessage;
      if (message && message.content) {
        message = await encryptionService.processMessage(message);

      }
      
      // Emit to listeners
      this._newMessageListeners.forEach(cb => {
        try { cb(message); } catch (e) { console.error(e); }
      });
      window.dispatchEvent(new CustomEvent('newMessage', { detail: message }));
    }

    // Handle message status updates
    if (data.type === 'message_status') {
      this._messageUpdateListeners.forEach(cb => {
        try { cb(data); } catch (e) { console.error(e); }
      });
      window.dispatchEvent(new CustomEvent('messageStatus', { detail: data }));
    }

    // Handle reaction updates
    if (data.type === 'reaction_update') {

      // Emit to listeners
      this._messageUpdateListeners.forEach(cb => {
        try { cb(data); } catch (e) { console.error(e); }
      });
      window.dispatchEvent(new CustomEvent('reactionUpdate', { detail: data }));
    }
  }

  /**
   * Check if WebSocket is connected
   * @returns {boolean} Connection status
   */
  isSocketConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Disconnect WebSocket
   */
  disconnectWebSocket() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  // ==================
  // QUEUE METHODS
  // ==================

  /**
   * Add message to queue
   * @param {Object} message - Message object
   */
  addToQueue(message) {
    this.messageQueue.push(message);
  }

  /**
   * Remove message from queue
   * @param {string} messageId - Message ID
   */
  removeFromQueue(messageId) {
    this.messageQueue = this.messageQueue.filter(msg => msg.id !== messageId);
  }

  /**
   * Process queued messages when back online
   */
  async processMessageQueue() {
    if (!this.isOnline || this.messageQueue.length === 0) {
      return;
    }

    const queuedMessages = this.messageQueue.filter(msg => msg.status === 'queued');
    
    for (const message of queuedMessages) {
      try {
        message.status = 'pending';
        await this.sendViaREST(message);
      } catch (error) {
        await this.handleSendError(message, error);
      }
    }
  }

  /**
   * Handle send errors with retry logic
   * @param {Object} message - Message object
   * @param {Error} error - Error object
   * @returns {Object} Message with error status
   */
  async handleSendError(message, error) {
    message.retries = (message.retries || 0) + 1;

    if (message.retries < this.retryAttempts) {
      // Exponential backoff retry
      const delay = this.reconnectDelay * Math.pow(2, message.retries);
      setTimeout(async () => {
        try {
          message.status = 'pending';
          await this.sendViaREST(message);
        } catch (retryError) {
          await this.handleSendError(message, retryError);
        }
      }, delay);
      
      message.status = 'retrying';
    } else {
      message.status = 'failed';
      message.error = error.message;
      console.error('Message send failed after retries:', error);
    }

    return message;
  }

  // ==================
  // MESSAGE EDIT METHODS
  // ==================

  /**
   * Edit a message
   * @param {number} messageId - Message ID to edit
   * @param {string} newContent - New message content
   * @returns {Promise<Object>} Updated message response
   */
  async editMessage(messageId, newContent) {
    try {
      if (!newContent.trim()) {
        throw new Error('Le contenu du message ne peut pas être vide');
      }

      if (newContent.length > 2000) {
        throw new Error('Le message ne peut pas dépasser 2000 caractères');
      }

      const response = await axios.put(
        `${config.backendUrl}/messaging/messages/${messageId}/`,
        { content: newContent },
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );

      // Notify listeners about the message update
      this._messageUpdateListeners.forEach(listener => {
        try {
          listener(response.data);
        } catch (error) {
          console.error('Error in message update listener:', error);
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error editing message:', error);
      
      // Return user-friendly error message
      if (error.response?.status === 403) {
        throw new Error('Vous ne pouvez plus modifier ce message (délai dépassé)');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Contenu du message invalide');
      } else {
        throw new Error('Erreur lors de la modification du message');
      }
    }
  }

  /**
   * Check if a message can be edited by the current user
   * @param {Object} message - Message object
   * @param {Object} currentUser - Current user object
   * @returns {boolean} Whether the message can be edited
   */
  canEditMessage(message, currentUser) {
    if (!message || !currentUser) return false;
    
    // Only sender can edit - compare as strings to handle both UUIDs and numeric IDs
    const messageSender = message.sender?.id || message.sender;
    if (String(messageSender) !== String(currentUser.id)) return false;
    
    // Cannot edit deleted messages
    if (message.is_deleted) return false;
    
    // Cannot edit audio messages
    if (message.audio) return false;
    
    // Check if within 15-minute time limit
    const messageTime = new Date(message.timestamp);
    const now = new Date();
    const timeDiff = (now - messageTime) / (1000 * 60); // difference in minutes
    
    return timeDiff <= 15;
  }

  /**
   * Delete a message (soft delete)
   * @param {number} messageId - Message ID to delete
   * @returns {Promise<Object>} Delete response
   */
  async deleteMessage(messageId) {
    try {
      const response = await axios.delete(
        `${config.backendUrl}/messaging/messages/${messageId}/`,
        {
          headers: this.getAuthHeaders(),
        }
      );

      // Notify listeners about the message update
      this._messageUpdateListeners.forEach(listener => {
        try {
          listener({ id: messageId, is_deleted: true });
        } catch (error) {
          console.error('Error in message update listener:', error);
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error deleting message:', error);
      
      if (error.response?.status === 403) {
        throw new Error('Vous ne pouvez pas supprimer ce message');
      } else {
        throw new Error('Erreur lors de la suppression du message');
      }
    }
  }

  /**
   * Check if a message can be deleted by the current user
   * @param {Object} message - Message object
   * @param {Object} currentUser - Current user object
   * @returns {boolean} Whether the message can be deleted
   */
  canDeleteMessage(message, currentUser) {
    if (!message || !currentUser) return false;
    
    // Only sender can delete - compare as strings to handle both UUIDs and numeric IDs
    const messageSender = message.sender?.id || message.sender;
    if (String(messageSender) !== String(currentUser.id)) return false;
    
    // Cannot delete already deleted messages
    if (message.is_deleted) return false;
    
    return true;
  }

  // =====================
  // MESSAGE REACTIONS
  // =====================

  /**
   * Toggle a reaction on a message
   * @param {number} messageId - Message ID
   * @param {string} emoji - Emoji to toggle
   * @returns {Promise<Object>} Reaction response
   */
  async toggleReaction(messageId, emoji) {
    try {
      const response = await axios.post(
        `${config.backendUrl}/messaging/messages/${messageId}/reactions/toggle/`,
        { emoji },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling reaction:', error);
      throw error;
    }
  }

  /**
   * Get all reactions for a message
   * @param {number} messageId - Message ID
   * @returns {Promise<Object>} Reactions response
   */
  async getMessageReactions(messageId) {
    try {
      const response = await axios.get(
        `${config.backendUrl}/messaging/messages/${messageId}/reactions/`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting message reactions:', error);
      throw error;
    }
  }

  // ===============
  // UTILITY METHODS
  // ===============

  /**
   * Handle API errors consistently
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.detail || 'Erreur du serveur';
      
      if (status === 401) {
        // Unauthorized - redirect to login
        window.dispatchEvent(new CustomEvent('unauthorized'));
      }
      
      return new Error(`${status}: ${message}`);
    } else if (error.request) {
      // Network error
      return new Error('Erreur de réseau. Vérifiez votre connexion.');
    } else {
      // Other error
      return new Error(error.message || 'Une erreur inattendue s\'est produite.');
    }
  }

  /**
   * Get message queue status
   * @returns {Object} Queue status
   */
  getQueueStatus() {
    return {
      total: this.messageQueue.length,
      pending: this.messageQueue.filter(msg => msg.status === 'pending').length,
      queued: this.messageQueue.filter(msg => msg.status === 'queued').length,
      failed: this.messageQueue.filter(msg => msg.status === 'failed').length,
      retrying: this.messageQueue.filter(msg => msg.status === 'retrying').length,
    };
  }

  // Event subscription methods
  onNewMessage(callback) {
    this._newMessageListeners.add(callback);
  }
  offNewMessage(callback) {
    this._newMessageListeners.delete(callback);
  }
  onMessageUpdate(callback) {
    this._messageUpdateListeners.add(callback);
  }
  offMessageUpdate(callback) {
    this._messageUpdateListeners.delete(callback);
  }

  // ==================
  // THREAD METHODS
  // ==================

  /**
   * Get all messages in a thread
   * @param {number} messageId - Message ID
   * @returns {Promise<Object>} Thread response
   */
  async getMessageThread(messageId) {
    try {
      const response = await axios.get(
        `${config.backendUrl}/messaging/messages/${messageId}/thread/`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting message thread:', error);
      throw error;
    }
  }

  /**
   * Reply to a message
   * @param {number} messageId - Original message ID
   * @param {string} content - Reply content
   * @returns {Promise<Object>} Reply response
   */
  async replyToMessage(messageId, content) {
    try {
      const response = await axios.post(
        `${config.backendUrl}/messaging/messages/${messageId}/reply/`,
        { content },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error replying to message:', error);
      throw error;
    }
  }
}

// Create singleton instance
const messagingService = new ModernMessagingService();

export default messagingService;
